import { Link } from "wouter";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  breadcrumbSchema,
  personSchema,
  webPageSchema,
} from "@/lib/seo";

export default function EditorialProcess() {
  const TITLE = "Editorial Process and Standards | Shariq Refai, MD, MBA";
  const DESCRIPTION =
    "How essays and educational content on shariqrefai.com are authored, reviewed, sourced, and updated. The editorial standards behind every article.";

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Editorial Process", path: "/editorial-process" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/editorial-process"
        schema={[
          personSchema,
          webPageSchema({
            path: "/editorial-process",
            title: TITLE,
            description: DESCRIPTION,
            type: "AboutPage",
            breadcrumbs: crumbs,
          }),
          breadcrumbSchema(crumbs),
        ]}
      />

      <article className="container mx-auto max-w-3xl px-4 md:px-8 py-16 md:py-24">
        <Breadcrumbs
          items={crumbs.map((c) => ({ name: c.name, href: c.path }))}
        />
        <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight mb-8">
          Editorial Process and Standards
        </h1>
        <p className="text-lg text-secondary font-light leading-relaxed mb-12">
          The standards behind every essay, book page, and educational piece
          published on this site.
        </p>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Who Writes the Content
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Every essay, book page, and educational piece on shariqrefai.com
            is authored by Shariq Refai, MD, MBA, board-certified
            psychiatrist and founder of shrinkMD. The voice is intentional.
            The work is editorial. The author is named on every page.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Who Reviews the Content
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Every essay is editorially reviewed by Dr. Refai before
            publication. Each carries a published date and a last-reviewed
            date. The phrasing used on essays is "Authored and editorially
            reviewed by Shariq Refai, MD, MBA," which is honest about the
            fact that the author and the reviewer are the same person. See
            the{" "}
            <a
              href="/medical-review-board"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Medical Review Board page
            </a>
            {" "}for reviewer roles and standards.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Sources and Citations
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Where essays cite specific clinical research, neuroscience
            findings, or treatment frameworks, sources are linked inline or
            listed in a References section at the end of the article.
            Preferred sources include peer-reviewed primary literature
            (PubMed-indexed papers), institutional sources (NIMH, NIH, APA,
            ABPN, WHO, CDC), and practice guidelines (APA, AAFP, USPSTF).
          </p>
          <p className="text-secondary font-light leading-relaxed">
            Where essays make experiential or observational claims, they're
            framed as editorial perspective grounded in years of clinical
            practice, not as cited research.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Annual Review
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Every published essay is reviewed at least once per year for
            clinical accuracy and currency. If a meaningful change is made,
            the "last reviewed" date on the article is updated to reflect
            the review.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Correction Policy
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Errors are flagged and corrected promptly. Material corrections
            are noted at the bottom of the affected article with the date of
            correction and a brief description of what changed. Minor copy
            or typography fixes are made silently.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            If you've found an error, please report it through the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>{" "}
            with "Other" selected as the inquiry type.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Advertising and Sponsorship Policy
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            shariqrefai.com doesn't run ads. It doesn't accept sponsored
            content. It doesn't take affiliate commissions on books or any
            external products. All third-party links are editorial.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Disclosure Policy
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Dr. Refai is the founder of, and holds a material ownership
            interest in, shrinkMD. He's the founder of shrinQ, an independent self-guided digital mental wellness program. He's the creator of the Unstuck app. He's
            the author of three books published through shrinkMD Publishing.
            These relationships are disclosed at every relevant reference
            across the site.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            Dr. Refai serves as the medical editor for four independent
            editorial publications, AnxietyResource.org,
            DepressionResource.org, AnxietyResearch.org, and
            PsychiatryRx.org. These publications are operated separately
            from shariqrefai.com.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Reader Feedback
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Feedback, questions, and suggestions are welcome through the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>
            . Reader feedback informs which topics get covered next and
            which existing essays get expanded.
          </p>
        </section>
      </article>
      <RelatedPages
        entries={[
          { href: "/medical-reviewer", label: "Medical reviewer", why: "The named physician who reviews clinical content." },
          { href: "/evidence", label: "How evidence is evaluated", why: "The hierarchy applied to every claim." },
          { href: "/corrections", label: "Corrections & updates policy", why: "How mistakes are corrected and updates logged." },
          { href: "/disclosures", label: "Relationships & financial disclosure", why: "The financial context behind the editorial voice." },
        ]}
      />
    </SiteLayout>
  );
}
